// ═══════════════════════════════════════════════════════════════════════
// Part 9: Export History Panel (with Undo Last Export)
// ═══════════════════════════════════════════════════════════════════════
// Shows recent export runs with search, filtering, rerun capability,
// and the ability to undo/reverse the most recent export run.

import React, { useState, useEffect, useCallback, useMemo } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  History, Search, FileText, FileSpreadsheet, Hash, RefreshCw,
  Loader2, ChevronDown, ChevronRight, RotateCcw, StickyNote,
  Calendar, User, AlertTriangle, TrendingUp, Download, Eye, X,
  Undo2, Trash2, ShieldAlert, CheckCircle2, XCircle, Clock, Pencil,
  AlertCircle, Ban,
} from 'lucide-react';
import { toast } from '@/components/ui/use-toast';
import { supabase } from '@/lib/supabase';
import { formatDateTime } from '@/lib/dates';
import type { ExportRun } from './types';

// ─── Props ──────────────────────────────────────────────────────────

interface ExportHistoryProps {
  open: boolean;
  onClose: () => void;
  onLoadSettings: (run: ExportRun) => void;
}

// ─── Invoice Record Info (for undo preview) ─────────────────────────

interface InvoiceRecordInfo {
  id: string;
  tc_id: string;
  job_id: string;
  invoice_number: number;
  invoice_group_key: string | null;
}

// ─── Helpers ────────────────────────────────────────────────────────

function relativeTime(dateStr: string): string {
  const now = new Date();
  const d = new Date(dateStr);
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHrs = Math.floor(diffMins / 60);
  if (diffHrs < 24) return `${diffHrs}h ago`;
  const diffDays = Math.floor(diffHrs / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return formatDateTime(dateStr);
}

// ─── Component ──────────────────────────────────────────────────────

const ExportHistory: React.FC<ExportHistoryProps> = ({ open, onClose, onLoadSettings }) => {
  const [runs, setRuns] = useState<ExportRun[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [templateFilter, setTemplateFilter] = useState('__all');
  const [targetFilter, setTargetFilter] = useState('__all');
  const [statusFilter, setStatusFilter] = useState<'__all' | 'active' | 'reversed'>('__all');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // ─── Undo State ─────────────────────────────────────────────────
  const [undoDialogOpen, setUndoDialogOpen] = useState(false);
  const [undoTargetRun, setUndoTargetRun] = useState<ExportRun | null>(null);
  const [undoInvoiceRecords, setUndoInvoiceRecords] = useState<InvoiceRecordInfo[]>([]);
  const [undoLoadingRecords, setUndoLoadingRecords] = useState(false);
  const [undoReason, setUndoReason] = useState('');
  const [undoConfirmed, setUndoConfirmed] = useState(false);
  const [undoExecuting, setUndoExecuting] = useState(false);

  // Load export runs
  const loadRuns = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('export_runs')
        .select('*')
        .order('exported_at', { ascending: false })
        .limit(100);
      if (error) throw error;
      setRuns((data || []) as ExportRun[]);
    } catch (err: any) {
      console.error('Failed to load export runs:', err);
      toast({ title: 'Error', description: 'Failed to load export history', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) loadRuns();
  }, [open, loadRuns]);

  // Unique template names and target systems for filters
  const templateNames = useMemo(() => {
    const names = new Set(runs.map(r => r.template_name_snapshot));
    return Array.from(names).sort();
  }, [runs]);

  const targetSystems = useMemo(() => {
    const systems = new Set(runs.map(r => r.target_system_snapshot).filter(Boolean));
    return Array.from(systems).sort() as string[];
  }, [runs]);

  // Filtered runs
  const filteredRuns = useMemo(() => {
    return runs.filter(r => {
      if (templateFilter !== '__all' && r.template_name_snapshot !== templateFilter) return false;
      if (targetFilter !== '__all' && r.target_system_snapshot !== targetFilter) return false;
      if (statusFilter !== '__all' && (r.status || 'active') !== statusFilter) return false;
      if (search.trim()) {
        const s = search.toLowerCase();
        return (
          r.template_name_snapshot.toLowerCase().includes(s) ||
          r.file_name.toLowerCase().includes(s) ||
          (r.notes || '').toLowerCase().includes(s) ||
          r.exported_by.toLowerCase().includes(s)
        );
      }
      return true;
    });
  }, [runs, templateFilter, targetFilter, statusFilter, search]);

  // Most recent active run (eligible for undo)
  const mostRecentActiveRun = useMemo(() => {
    return runs.find(r => (r.status || 'active') === 'active') || null;
  }, [runs]);

  // Count of reversed runs
  const reversedCount = useMemo(() => {
    return runs.filter(r => r.status === 'reversed').length;
  }, [runs]);

  const handleLoadSettings = (run: ExportRun) => {
    onLoadSettings(run);
    onClose();
    toast({
      title: 'Settings Loaded',
      description: `Restored export settings from "${run.template_name_snapshot}" run on ${formatDateTime(run.exported_at)}`,
    });
  };

  // ─── Undo Logic ─────────────────────────────────────────────────

  const openUndoDialog = useCallback(async (run: ExportRun) => {
    setUndoTargetRun(run);
    setUndoReason('');
    setUndoConfirmed(false);
    setUndoInvoiceRecords([]);
    setUndoDialogOpen(true);

    // Fetch invoice records for this run
    setUndoLoadingRecords(true);
    try {
      const { data, error } = await supabase
        .from('job_invoice_records')
        .select('id, tc_id, job_id, invoice_number, invoice_group_key')
        .eq('export_run_id', run.id)
        .order('invoice_number', { ascending: true })
        .limit(500);

      if (error) throw error;
      setUndoInvoiceRecords((data || []) as InvoiceRecordInfo[]);
    } catch (err: any) {
      console.error('Failed to load invoice records for undo:', err);
      toast({
        title: 'Warning',
        description: 'Could not load invoice records for this run. You can still proceed with the reversal.',
        variant: 'destructive',
      });
    } finally {
      setUndoLoadingRecords(false);
    }
  }, []);

  const executeUndo = useCallback(async () => {
    if (!undoTargetRun || !undoReason.trim()) return;

    setUndoExecuting(true);
    try {
      // Step 1: Delete invoice records for this export run
      const { error: deleteError, count: deletedCount } = await supabase
        .from('job_invoice_records')
        .delete({ count: 'exact' })
        .eq('export_run_id', undoTargetRun.id);

      if (deleteError) throw deleteError;

      // Step 2: Mark the export run as reversed
      const { error: updateError } = await supabase
        .from('export_runs')
        .update({
          status: 'reversed',
          reversed_at: new Date().toISOString(),
          reversed_by: 'admin',
          reversal_reason: undoReason.trim(),
        })
        .eq('id', undoTargetRun.id);

      if (updateError) throw updateError;

      toast({
        title: 'Export Reversed',
        description: `Successfully reversed export "${undoTargetRun.template_name_snapshot}". ${deletedCount ?? undoInvoiceRecords.length} invoice record(s) deleted.`,
      });

      // Close dialog and refresh
      setUndoDialogOpen(false);
      setUndoTargetRun(null);
      await loadRuns();
    } catch (err: any) {
      console.error('Failed to undo export:', err);
      toast({
        title: 'Undo Failed',
        description: err.message || 'Failed to reverse the export run. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setUndoExecuting(false);
    }
  }, [undoTargetRun, undoReason, undoInvoiceRecords.length, loadRuns]);

  // Unique invoice numbers in the undo target
  const undoInvoiceNumbers = useMemo(() => {
    const nums = new Set(undoInvoiceRecords.map(r => r.invoice_number));
    return Array.from(nums).sort((a, b) => a - b);
  }, [undoInvoiceRecords]);

  // Unique job IDs in the undo target
  const undoUniqueJobIds = useMemo(() => {
    const ids = new Set(undoInvoiceRecords.map(r => r.job_id));
    return Array.from(ids);
  }, [undoInvoiceRecords]);

  const undoHasReason = undoReason.trim().length > 0;
  const undoCanExecute = undoHasReason && undoConfirmed && !undoExecuting;

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
        <DialogContent className="bg-card border-border max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-foreground flex items-center gap-2">
              <History className="w-5 h-5 text-primary" />
              Export History
            </DialogTitle>
            <DialogDescription className="text-muted-foreground text-xs">
              View previous export runs, review settings, reload configurations, or undo the last export
            </DialogDescription>
          </DialogHeader>

          {/* Filters */}
          <div className="flex items-center gap-2 mt-1">
            <div className="relative flex-1">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search by template, file name, notes..."
                className="dark-input text-xs h-8 pl-8"
              />
            </div>
            <Select value={templateFilter} onValueChange={setTemplateFilter}>
              <SelectTrigger className="dark-input text-xs h-8 w-[180px]">
                <SelectValue placeholder="All Templates" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">All Templates</SelectItem>
                {templateNames.map(n => (
                  <SelectItem key={n} value={n}>{n}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={targetFilter} onValueChange={setTargetFilter}>
              <SelectTrigger className="dark-input text-xs h-8 w-[120px]">
                <SelectValue placeholder="All Systems" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">All Systems</SelectItem>
                {targetSystems.map(s => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as any)}>
              <SelectTrigger className="dark-input text-xs h-8 w-[110px]">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="reversed">Reversed</SelectItem>
              </SelectContent>
            </Select>
            <Button size="sm" variant="ghost" onClick={loadRuns} disabled={loading} className="h-8 px-2">
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>

          {/* Results */}
          <div className="flex-1 overflow-y-auto mt-2 min-h-0">
            {loading && runs.length === 0 ? (
              <div className="flex items-center justify-center py-16 text-muted-foreground">
                <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading history...
              </div>
            ) : filteredRuns.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                <History className="w-10 h-10 mb-3 opacity-30" />
                <p className="text-sm">{runs.length === 0 ? 'No export runs recorded yet.' : 'No runs match your filters.'}</p>
              </div>
            ) : (
              <div className="space-y-1.5">
                {filteredRuns.map((run, index) => {
                  const isExpanded = expandedId === run.id;
                  const isReversed = run.status === 'reversed';
                  const isMostRecentActive = mostRecentActiveRun?.id === run.id;
                  const hasInvoices = run.starting_invoice_number != null;

                  return (
                    <div
                      key={run.id}
                      className={`border rounded-md transition-colors ${
                        isReversed
                          ? 'border-red-800/40 bg-red-950/10 opacity-75'
                          : isExpanded
                            ? 'border-primary/40 bg-primary/5'
                            : 'border-border/60 hover:border-border'
                      }`}
                    >
                      {/* Run Header */}
                      <div
                        className="flex items-center gap-2 px-3 py-2 cursor-pointer"
                        onClick={() => setExpandedId(isExpanded ? null : run.id)}
                      >
                        {run.file_type === 'xlsx' ? (
                          <FileSpreadsheet className={`w-4 h-4 flex-shrink-0 ${isReversed ? 'text-red-400/50' : 'text-blue-400'}`} />
                        ) : (
                          <FileText className={`w-4 h-4 flex-shrink-0 ${isReversed ? 'text-red-400/50' : 'text-green-400'}`} />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className={`text-xs font-semibold truncate ${isReversed ? 'text-muted-foreground line-through' : 'text-foreground'}`}>
                              {run.template_name_snapshot}
                            </span>
                            {run.target_system_snapshot && (
                              <Badge className="bg-secondary text-muted-foreground text-[9px] px-1 py-0 flex-shrink-0">{run.target_system_snapshot}</Badge>
                            )}
                            <Badge className={`text-[9px] px-1 py-0 flex-shrink-0 ${
                              isReversed
                                ? 'bg-red-900/30 text-red-400/70 border-red-700/30'
                                : run.file_type === 'xlsx'
                                  ? 'bg-blue-900/30 text-blue-400 border-blue-700/40'
                                  : 'bg-green-900/30 text-green-400 border-green-700/40'
                            }`}>
                              {run.file_type.toUpperCase()}
                            </Badge>
                            {isReversed && (
                              <Badge className="bg-red-900/40 text-red-400 border-red-700/50 text-[9px] px-1.5 py-0 flex-shrink-0">
                                <Undo2 className="w-2.5 h-2.5 mr-0.5" />
                                REVERSED
                              </Badge>
                            )}
                            {isMostRecentActive && !isReversed && (
                              <Badge className="bg-emerald-900/30 text-emerald-400 border-emerald-700/40 text-[9px] px-1.5 py-0 flex-shrink-0">
                                Latest
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-3 text-[10px] text-muted-foreground mt-0.5">
                            <span className={`truncate max-w-[200px] ${isReversed ? 'line-through' : ''}`}>{run.file_name}</span>
                            <span>{run.row_count} rows</span>
                            {run.starting_invoice_number != null && (
                              <span className={isReversed ? 'text-red-400/50 line-through' : 'text-indigo-400'}>
                                Inv {run.starting_invoice_number}-{run.ending_invoice_number}
                              </span>
                            )}
                            {run.grand_total_snapshot != null && (
                              <span className={isReversed ? 'line-through' : ''}>${Number(run.grand_total_snapshot).toFixed(2)}</span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <div className="text-right">
                            <p className="text-[10px] text-muted-foreground">{relativeTime(run.exported_at)}</p>
                            <p className="text-[9px] text-muted-foreground/60">{run.exported_by}</p>
                          </div>
                          {isExpanded ? <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" /> : <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />}
                        </div>
                      </div>

                      {/* Expanded Details */}
                      {isExpanded && (
                        <div className="px-3 pb-3 pt-1 border-t border-border/40 space-y-3">
                          {/* Reversal Info Banner */}
                          {isReversed && (
                            <div className="bg-red-900/15 border border-red-700/30 rounded px-3 py-2 space-y-1.5">
                              <div className="flex items-center gap-2">
                                <Undo2 className="w-4 h-4 text-red-400" />
                                <span className="text-xs font-semibold text-red-400">This export has been reversed</span>
                              </div>
                              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[10px]">
                                <div className="flex justify-between">
                                  <span className="text-red-400/60">Reversed At:</span>
                                  <span className="text-red-400/80">{run.reversed_at ? formatDateTime(run.reversed_at) : '—'}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-red-400/60">Reversed By:</span>
                                  <span className="text-red-400/80">{run.reversed_by || '—'}</span>
                                </div>
                              </div>
                              {run.reversal_reason && (
                                <div className="mt-1">
                                  <span className="text-[9px] text-red-400/60 uppercase font-medium">Reason:</span>
                                  <p className="text-[10px] text-red-400/80 mt-0.5">{run.reversal_reason}</p>
                                </div>
                              )}
                              <p className="text-[9px] text-red-400/50 mt-1">
                                All invoice records created by this run have been deleted. The export file itself was not removed from the user's downloads.
                              </p>
                            </div>
                          )}

                          {/* Details Grid */}
                          <div className="grid grid-cols-3 gap-x-6 gap-y-1.5 text-xs">
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Exported:</span>
                              <span className="text-foreground">{formatDateTime(run.exported_at)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">By:</span>
                              <span className="text-foreground">{run.exported_by}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">File:</span>
                              <span className="text-foreground truncate ml-2">{run.file_name}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Rows:</span>
                              <span className="text-foreground">{run.row_count.toLocaleString()}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Columns:</span>
                              <span className="text-foreground">{run.column_count}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Scope:</span>
                              <span className="text-foreground">{run.export_scope}</span>
                            </div>
                            {run.starting_invoice_number != null && (
                              <>
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Invoice Range:</span>
                                  <span className={isReversed ? 'text-red-400/50 line-through' : 'text-indigo-400'}>
                                    {run.starting_invoice_number} - {run.ending_invoice_number}
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Invoice Groups:</span>
                                  <span className={isReversed ? 'text-red-400/50' : 'text-indigo-400'}>{run.invoice_group_count}</span>
                                </div>
                                {run.invoice_grouping_mode && (
                                  <div className="flex justify-between">
                                    <span className="text-muted-foreground">Grouping:</span>
                                    <span className="text-foreground">{run.invoice_grouping_mode}</span>
                                  </div>
                                )}
                              </>
                            )}
                            {run.margin_rule_count > 0 && (
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Margin Rules:</span>
                                <span className="text-emerald-400">{run.margin_rule_count} active</span>
                              </div>
                            )}
                            {run.flagged_row_count > 0 && (
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Flagged:</span>
                                <span className="text-amber-400">{run.flagged_row_count}</span>
                              </div>
                            )}
                            {run.excluded_row_count > 0 && (
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Excluded:</span>
                                <span className="text-red-400">{run.excluded_row_count}</span>
                              </div>
                            )}
                            {run.grand_total_snapshot != null && (
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Grand Total:</span>
                                <span className="text-foreground font-medium">${Number(run.grand_total_snapshot).toFixed(2)}</span>
                              </div>
                            )}
                            {run.sheet_name && (
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Sheet:</span>
                                <span className="text-foreground">{run.sheet_name}</span>
                              </div>
                            )}
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Status:</span>
                              <span className={isReversed ? 'text-red-400 font-medium' : 'text-green-400 font-medium'}>
                                {isReversed ? 'Reversed' : 'Active'}
                              </span>
                            </div>
                          </div>

                          {/* Notes */}
                          {run.notes && (
                            <div className="bg-secondary/20 border border-border/40 rounded px-2.5 py-1.5">
                              <div className="flex items-center gap-1 mb-0.5">
                                <StickyNote className="w-3 h-3 text-muted-foreground" />
                                <span className="text-[10px] text-muted-foreground uppercase font-medium">Notes</span>
                              </div>
                              <p className="text-xs text-foreground whitespace-pre-wrap">{run.notes}</p>
                            </div>
                          )}

                          {/* Snapshot indicators */}
                          <div className="flex flex-wrap gap-1.5">
                            {run.template_columns_snapshot && (
                              <Badge className="bg-blue-900/20 text-blue-400 border-blue-700/30 text-[9px] px-1.5 py-0">
                                Columns Snapshot
                              </Badge>
                            )}
                            {run.margin_rules_snapshot && (
                              <Badge className="bg-emerald-900/20 text-emerald-400 border-emerald-700/30 text-[9px] px-1.5 py-0">
                                Margin Rules Snapshot
                              </Badge>
                            )}
                            {run.invoice_settings_snapshot && (
                              <Badge className="bg-indigo-900/20 text-indigo-400 border-indigo-700/30 text-[9px] px-1.5 py-0">
                                Invoice Settings Snapshot
                              </Badge>
                            )}
                            {run.anomaly_config_snapshot && (
                              <Badge className="bg-amber-900/20 text-amber-400 border-amber-700/30 text-[9px] px-1.5 py-0">
                                Anomaly Config Snapshot
                              </Badge>
                            )}
                            {run.filters_snapshot && (
                              <Badge className="bg-purple-900/20 text-purple-400 border-purple-700/30 text-[9px] px-1.5 py-0">
                                Filters Snapshot
                              </Badge>
                            )}
                          </div>

                          {/* Actions */}
                          <div className="flex items-center gap-2 pt-1 border-t border-border/30">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleLoadSettings(run)}
                              className="h-7 text-xs border-primary/40 text-primary hover:bg-primary/10"
                            >
                              <RotateCcw className="w-3 h-3 mr-1" />
                              Load Settings
                            </Button>

                            {/* Undo button — only on the most recent active run */}
                            {isMostRecentActive && !isReversed && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openUndoDialog(run);
                                }}
                                className="h-7 text-xs border-red-700/40 text-red-400 hover:bg-red-900/20 hover:text-red-300"
                              >
                                <Undo2 className="w-3 h-3 mr-1" />
                                Undo Export
                              </Button>
                            )}

                            <span className="text-[9px] text-muted-foreground/60 flex-1">
                              {isReversed
                                ? 'This run has been reversed — invoice records were deleted'
                                : isMostRecentActive
                                  ? 'Most recent active run — can be undone'
                                  : 'Restores template, export settings, and invoice config from this run'
                              }
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between pt-2 border-t border-border/40 mt-2">
            <div className="flex items-center gap-3">
              <span className="text-[10px] text-muted-foreground">
                {filteredRuns.length} of {runs.length} runs shown
              </span>
              {reversedCount > 0 && (
                <Badge className="bg-red-900/20 text-red-400/70 border-red-700/30 text-[9px] px-1.5 py-0">
                  {reversedCount} reversed
                </Badge>
              )}
            </div>
            <Button variant="ghost" onClick={onClose} className="text-muted-foreground text-xs">
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ═══ Undo Confirmation Dialog ═══ */}
      <Dialog open={undoDialogOpen} onOpenChange={(v) => { if (!v && !undoExecuting) { setUndoDialogOpen(false); setUndoTargetRun(null); } }}>
        <DialogContent className="bg-card border-border max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-foreground flex items-center gap-2">
              <Undo2 className="w-5 h-5 text-red-400" />
              Undo Last Export
            </DialogTitle>
            <DialogDescription className="text-muted-foreground text-xs">
              Reverse the most recent export run and delete its invoice records
            </DialogDescription>
          </DialogHeader>

          {undoTargetRun && (
            <div className="space-y-4 mt-2">

              {/* Warning Banner */}
              <div className="bg-red-900/15 border border-red-700/30 rounded-md px-3 py-2.5">
                <div className="flex items-start gap-2">
                  <ShieldAlert className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs font-semibold text-red-400">
                      This action cannot be undone
                    </p>
                    <p className="text-[10px] text-red-400/70 mt-0.5">
                      Reversing this export will permanently delete all invoice records created by this run from the database.
                      The downloaded export file will not be affected — you should manually delete it if needed.
                    </p>
                  </div>
                </div>
              </div>

              {/* Export Run Summary */}
              <div className="border border-border/50 rounded-md overflow-hidden">
                <div className="bg-secondary/20 px-3 py-1.5 border-b border-border/40">
                  <span className="text-[10px] text-muted-foreground uppercase font-medium">Export Run to Reverse</span>
                </div>
                <div className="px-3 py-2 space-y-1.5">
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Template:</span>
                      <span className="text-foreground font-medium">{undoTargetRun.template_name_snapshot}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Exported:</span>
                      <span className="text-foreground">{formatDateTime(undoTargetRun.exported_at)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">File:</span>
                      <span className="text-foreground truncate ml-2">{undoTargetRun.file_name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Rows:</span>
                      <span className="text-foreground">{undoTargetRun.row_count.toLocaleString()}</span>
                    </div>
                    {undoTargetRun.starting_invoice_number != null && (
                      <>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Invoice Range:</span>
                          <span className="text-indigo-400">{undoTargetRun.starting_invoice_number} - {undoTargetRun.ending_invoice_number}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Invoice Groups:</span>
                          <span className="text-indigo-400">{undoTargetRun.invoice_group_count}</span>
                        </div>
                      </>
                    )}
                    {undoTargetRun.grand_total_snapshot != null && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Grand Total:</span>
                        <span className="text-foreground font-medium">${Number(undoTargetRun.grand_total_snapshot).toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">By:</span>
                      <span className="text-foreground">{undoTargetRun.exported_by}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* What Will Be Undone */}
              <div className="border border-border/50 rounded-md overflow-hidden">
                <div className="bg-red-900/10 px-3 py-1.5 border-b border-border/40">
                  <span className="text-[10px] text-red-400 uppercase font-medium">What Will Be Deleted</span>
                </div>
                <div className="px-3 py-2">
                  {undoLoadingRecords ? (
                    <div className="flex items-center gap-2 py-3 justify-center text-muted-foreground">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span className="text-xs">Loading invoice records...</span>
                    </div>
                  ) : undoInvoiceRecords.length === 0 ? (
                    <div className="flex items-center gap-2 py-2">
                      <AlertCircle className="w-3.5 h-3.5 text-amber-400" />
                      <span className="text-xs text-amber-400">
                        No invoice records found for this export run. The run will still be marked as reversed.
                      </span>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {/* Summary Stats */}
                      <div className="flex flex-wrap items-center gap-3 text-xs">
                        <div className="flex items-center gap-1.5">
                          <Trash2 className="w-3.5 h-3.5 text-red-400" />
                          <span className="text-red-400 font-semibold">{undoInvoiceRecords.length}</span>
                          <span className="text-muted-foreground">invoice record{undoInvoiceRecords.length !== 1 ? 's' : ''} to delete</span>
                        </div>
                        <div className="h-3 w-px bg-border" />
                        <div className="flex items-center gap-1">
                          <span className="text-muted-foreground">Affecting</span>
                          <span className="text-foreground font-semibold">{undoUniqueJobIds.length}</span>
                          <span className="text-muted-foreground">unique job{undoUniqueJobIds.length !== 1 ? 's' : ''}</span>
                        </div>
                        {undoInvoiceNumbers.length > 0 && (
                          <>
                            <div className="h-3 w-px bg-border" />
                            <div className="flex items-center gap-1">
                              <Hash className="w-3 h-3 text-indigo-400" />
                              <span className="text-indigo-400">
                                Invoice{undoInvoiceNumbers.length !== 1 ? 's' : ''} {undoInvoiceNumbers[0]}
                                {undoInvoiceNumbers.length > 1 ? ` - ${undoInvoiceNumbers[undoInvoiceNumbers.length - 1]}` : ''}
                              </span>
                            </div>
                          </>
                        )}
                      </div>

                      {/* Job ID List */}
                      <div className="max-h-[120px] overflow-y-auto border border-border/30 rounded">
                        <table className="w-full text-[10px]">
                          <thead className="sticky top-0 bg-card z-10">
                            <tr className="border-b border-border/40">
                              <th className="text-left py-1 px-2 text-muted-foreground font-medium">Job ID</th>
                              <th className="text-center py-1 px-2 text-muted-foreground font-medium">Invoice #</th>
                              <th className="text-left py-1 px-2 text-muted-foreground font-medium">Group Key</th>
                            </tr>
                          </thead>
                          <tbody>
                            {undoInvoiceRecords.slice(0, 30).map(rec => (
                              <tr key={rec.id} className="border-b border-border/20 hover:bg-red-900/5">
                                <td className="py-0.5 px-2 font-mono text-foreground">{rec.job_id}</td>
                                <td className="py-0.5 px-2 text-center">
                                  <Badge className="bg-indigo-900/30 text-indigo-400 border-indigo-700/40 text-[9px] px-1 py-0 font-mono">
                                    {rec.invoice_number}
                                  </Badge>
                                </td>
                                <td className="py-0.5 px-2 text-muted-foreground truncate max-w-[150px]">
                                  {rec.invoice_group_key || '—'}
                                </td>
                              </tr>
                            ))}
                            {undoInvoiceRecords.length > 30 && (
                              <tr>
                                <td colSpan={3} className="py-1 text-muted-foreground/60 text-center italic">
                                  ...and {undoInvoiceRecords.length - 30} more records
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Reason (required) */}
              <div>
                <div className="flex items-center gap-1.5 mb-1">
                  <Pencil className="w-3 h-3 text-red-400" />
                  <Label className="text-[10px] text-red-400 uppercase tracking-wider font-semibold">
                    Reversal Reason
                  </Label>
                  <span className="text-[9px] text-red-400/60">(required)</span>
                </div>
                <Textarea
                  value={undoReason}
                  onChange={e => {
                    setUndoReason(e.target.value);
                    setUndoConfirmed(false);
                  }}
                  placeholder="e.g. Export was run by mistake, Wrong date range selected, Need to re-run with corrected margin rules..."
                  className="dark-input text-xs min-h-[50px] resize-none border-red-700/30 focus:border-red-600/50"
                  disabled={undoExecuting}
                />
                {!undoHasReason && (
                  <p className="text-[9px] text-red-400/60 mt-0.5">
                    You must provide a reason for reversing this export
                  </p>
                )}
              </div>

              {/* Confirmation Checkbox */}
              {undoHasReason && (
                <div
                  className={`flex items-start gap-2.5 rounded px-3 py-2 cursor-pointer transition-colors ${
                    undoConfirmed
                      ? 'bg-amber-900/15 border border-amber-700/30'
                      : 'bg-red-900/10 border border-red-700/30 hover:bg-red-900/15'
                  }`}
                  onClick={() => !undoExecuting && setUndoConfirmed(!undoConfirmed)}
                >
                  <Checkbox
                    checked={undoConfirmed}
                    onCheckedChange={(v) => !undoExecuting && setUndoConfirmed(!!v)}
                    className="mt-0.5 h-4 w-4"
                    disabled={undoExecuting}
                  />
                  <div>
                    <p className="text-xs font-medium text-foreground">
                      I understand this will permanently delete {undoInvoiceRecords.length} invoice record{undoInvoiceRecords.length !== 1 ? 's' : ''}
                    </p>
                    <p className="text-[10px] text-muted-foreground/70 mt-0.5">
                      The invoice numbers ({undoInvoiceNumbers.length > 0 ? `${undoInvoiceNumbers[0]}${undoInvoiceNumbers.length > 1 ? ` - ${undoInvoiceNumbers[undoInvoiceNumbers.length - 1]}` : ''}` : 'none'}) will be freed up for re-use in future exports.
                      The export run will be marked as "reversed" with the reason and timestamp recorded for audit purposes.
                      {undoInvoiceRecords.length > 0 && ' Jobs will no longer show as previously invoiced for this template.'}
                    </p>
                  </div>
                </div>
              )}

              {/* Confirmed status */}
              {undoConfirmed && undoHasReason && (
                <div className="flex items-center gap-2 bg-amber-900/10 border border-amber-700/20 rounded px-2.5 py-1.5">
                  <AlertTriangle className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" />
                  <span className="text-[10px] text-amber-400">
                    Ready to reverse — {undoInvoiceRecords.length} invoice record{undoInvoiceRecords.length !== 1 ? 's' : ''} will be deleted permanently
                  </span>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="mt-4 flex items-center gap-2">
            <Button
              variant="ghost"
              onClick={() => { setUndoDialogOpen(false); setUndoTargetRun(null); }}
              className="text-muted-foreground"
              disabled={undoExecuting}
            >
              Cancel
            </Button>

            {!undoCanExecute && (
              <Button disabled className="bg-red-900/30 text-red-400/60 border border-red-700/30 cursor-not-allowed">
                <Ban className="w-4 h-4 mr-1" />
                {!undoHasReason ? 'Reason Required' : 'Confirmation Required'}
              </Button>
            )}

            {undoCanExecute && (
              <Button
                onClick={executeUndo}
                disabled={undoExecuting}
                className="bg-red-600 hover:bg-red-700 text-white border border-red-500"
              >
                {undoExecuting ? (
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                ) : (
                  <Undo2 className="w-4 h-4 mr-1" />
                )}
                {undoExecuting
                  ? 'Reversing...'
                  : `Reverse Export (Delete ${undoInvoiceRecords.length} Record${undoInvoiceRecords.length !== 1 ? 's' : ''})`
                }
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ExportHistory;
